<template>
  <div id="template-tour" class="container pb-32 pb-lg-64">
    <v-loader v-if="status === 'loading'" />

    <template v-else>
      <div class="pt-40 pb-32 pb-lg-48">
        <div class="d-flex flex-column flex-md-row justify-content-between">
          <div class="d-flex justify-content-between align-items-center w-100 mb-24 mb-md-0">
            <div class="h4 ff-secondary mb-0 d-flex align-items-center">
              <div class="bg-secondary rounded-xs me-8">
                <v-icon icon="compass" size="xxl" space="p-12" />
              </div>

              {{ t(getTitle) }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!!tourIdAux"
        class="tour-steps-container d-flex align-items-center justify-content-center mb-48 flex-wrap"
      >
        <div v-for="step in tourSteps" :key="step.key" class="tour-step">
          <router-link
            :id="step.id"
            :aria-label="$t(step.translation)"
            :class="{ 'router-link-active': step.url === $route.name }"
            :to="{ name: step.url, params: getParams(step.url) }"
            class="btn btn-outline-primary"
            @click="actualStep = step.key"
          >
            {{ $t(step.translation) }}
          </router-link>
        </div>
      </div>

      <div class="d-flex justify-content-end mb-28">
        <v-button
          class="btn-icon w-fit align-self-end me-16"
          size="sm"
          :disabled="disableDuplicateBtn"
          variant="primary"
          @click="duplicateTemplate"
        >
          {{ $t("template.duplicateActivity") }}
          <span class="mi mi-copy text-sm ms-12" />
        </v-button>

        <v-button
          :disabled="caseCreateOpened || userStore.currentTemplate?.isCollaborator"
          class="btn-icon w-fit align-self-end"
          size="sm"
          variant="primary"
          @click="
            openOffCanvas('the-case-create-offcanvas');
            caseCreateOpened = true;
          "
        >
          {{ $t("template.createCaseWithTemplate") }}
          <v-icon icon="plus" size="sm" space="ms-12" />
        </v-button>
      </div>

      <router-view
        v-if="!!userStore.currentTemplate"
        :template="userStore.currentTemplate"
        @reload-template="loadTemplate"
      />
    </template>

    <div v-if="false" class="fixed-bottom bg-body">
      <div class="container-xl">
        <div class="tour-list-container">
          <v-button
            :aria-label="$t('tourClient.createTour')"
            class="btn-icon"
            size="sm"
            variant="outline-tertiary"
            @click="createTour"
          >
            <v-icon icon="plus" size="xs" space="me-8" />

            <span>{{ $t("tourClient.createTour") }}</span>
          </v-button>

          <router-link
            v-for="tour in tours"
            :key="tour.id"
            :aria-label="$t('tourClient.tour')"
            :class="{ active: tourIdAux === tour.id }"
            :to="{ name: 'tour.builder.show', params: { activitiesId: tour.id } }"
            class="btn btn-outline-tertiary btn-sm shadow-none"
          >
            <div class="d-flex">
              {{ tour.id }}

              <v-dropdown
                :id="`tour-dropdown-${tour.id}`"
                button-classes="text-sm fw-medium bg-transparent shadow-none btn-menu"
                class="ms-8"
                dropdown-classes="dropdown-tour dropdown-menu-end"
              >
                <template #content>
                  <li class="dropdown-list p-0">
                    <a
                      :aria-label="$t('tourClient.archiveTemplate')"
                      class="dropdown-item px-8 py-16"
                      href="#"
                      @click.prevent="archiveTemplate(tour.id)"
                    >
                      <span>{{ $t("tourClient.archiveTemplate") }}</span>
                    </a>
                  </li>
                </template>
              </v-dropdown>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <the-case-create-offcanvas
      v-if="offCanvasToShow === 'the-case-create-offcanvas'"
      :template-data="userStore.currentTemplate"
      @closed="
        closeOffCanvas();
        caseCreateOpened = false;
      "
    />
  </div>
</template>

<script lang="ts" setup>
import api from "@/api";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import { useAuthStore } from "@/stores/auth";
import Tour from "@/api/objects/Tour";
import VDropdown from "@/components/vendor/basic/dropdown/VDropdown.vue";
import { useUserStore } from "@/stores/user";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import TheCaseCreateOffcanvas from "@/components/case/TheCaseCreateOffcanvas.vue";
import { ref, watch, onUnmounted, computed, onMounted, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

interface TourStep {
  key: string;
  label: string;
  url: string;
  id: string;
  translation: string;
}

const props = defineProps({
  activitiesId: {
    type: String,
    required: true,
  },
});
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const toast = useToast();
const userStore = useUserStore();

const authStore = useAuthStore();
const { offCanvasToShow, openOffCanvas, closeOffCanvas } = useOffCanvasUtils();

const caseCreateOpened = ref<boolean>(false);
const disableDuplicateBtn = ref<boolean>(false);
const tourIdAux = ref<number | null>(null);
const status = ref<string>("loading");
const actualStep = ref<string>("setup");
const tours = ref<Tour[]>([]);
const tourSteps = ref<TourStep[]>([
  {
    key: "setup",
    label: "Setup",
    url: "activities.setup.show",
    id: "template-case-setup-v2",
    translation: "tourClient.setup",
  },
  {
    key: "builder",
    label: "Builder",
    url: "activities.builder.show",
    id: "template-builder-v2",
    translation: "tourClient.builder",
  },
]);

const duplicateTemplate = async () => {
  if (authStore.agency && props.activitiesId) {
    disableDuplicateBtn.value = true;

    try {
      const response = await api.activityService.duplicateActivity(authStore?.agency?.slug, props.activitiesId);

      toast.success(t("template.duplicatedActivitySuccess"), {
        position: "bottom-right",
      });
      toast.success(t("template.redirectingNewActivity"), { position: "bottom-right", timeout: 10000 });

      await router.push({
        name: "activities.setup.show",
        params: { activitiesId: response.id },
      });
    } catch (e: any) {
      console.error(e.response?.data);
      toast.error(e.response?.data?.message || "An error occurred.", {
        position: "bottom-right",
      });
    } finally {
      disableDuplicateBtn.value = false;
    }
  }
};

const loadTemplate = async () => {
  if (authStore.agency) {
    try {
      const tourData = {
        id: props.activitiesId,
        agency: authStore.agency.slug,
      };

      await userStore.setCurrentTemplateV2(tourData);

      tourIdAux.value = Number(props.activitiesId);
    } catch (e: any) {
      await router.replace({ name: "activities.index" });
    }
  }
};

const createTour = async () => {
  await $modal.confirm({
    title: t("tourClient.createTour"),
    text: t("tourClient.createTourText"),
    leftButtonClasses: "btn-tertiary",
    confirmButtonText: t("general.button.confirm"),
    confirmButtonCallback: doCreateTour,
  });
};

const doCreateTour = async () => {
  // if (!!this.agency) {
  //   const tour: Tour = await api.tour.create(this.agency.slug, Number(this.caseId), { status: 'initializing' });
  //
  //   this.$toast.success(tour.message);
  //
  //   await this.loadTemplate();
  //
  //   await this.$router.replace({
  //     name: 'tour.builder.show',
  //     params: { caseId: Number(this.caseId), templateId: tour.id },
  //   });
  // }
};

const archiveTemplate = async (activitiesId: string | number) => {
  await $modal.delete({
    title: t("template.archiveTitle"),
    text: t("template.archiveText"),
    deleteButtonText: t("general.button.archive"),
    deleteButtonLoadingText: t("general.button.archiving"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: () => doArchiveTemplate(activitiesId),
  });
};

// Function to perform archive action
const doArchiveTemplate = async (activitiesId: string | number) => {
  if (authStore.agency === null) {
    console.error("Agency is not available");
    return;
  }

  await api.template.archive(authStore.agency.slug, activitiesId);

  toast.success(t("general.shared.savedChanges"), {
    position: "bottom-right",
  });

  // Reload templates
  await loadTemplate();

  // TODO: Navigate to another tour
  // await router.replace({
  //   name: 'tour.builder.show',
  //   params: { caseId: Number(props.caseId), templateId: tour.id },
  // });
};

const getTitle = computed(() => {
  switch (props.actualStep) {
    case "setup":
      return "template.setup";
    case "builder":
      return "template.productBuilder";
    default:
      return "template.setup";
  }
});

const getParams = (url: string) => {
  return url === "activities.index" ? {} : { activitiesId: props.activitiesId };
};

onUnmounted(() => {
  userStore.setCurrentTour(null);
});

watch(
  () => props.activitiesId,
  async (newValue) => {
    status.value = "loading";

    tourIdAux.value = Number(newValue);

    await loadTemplate();

    status.value = "loaded";
  }
);

onMounted(async () => {
  if (route.name) {
    const stepAux = tourSteps.value.find((step) => step.url === route.name);

    if (stepAux) {
      actualStep.value = stepAux.key;

      await router.replace({
        name: route.name as string,
        params: { activitiesId: props.activitiesId },
      });
    } else {
      await router.replace({
        name: "activities.setup.show",
        params: { activitiesId: props.activitiesId },
      });
    }
  }

  await loadTemplate();

  status.value = "loaded";
});
</script>

<style lang="scss" scoped>
.tour-steps-container {
  gap: 1rem;
}
</style>
